import React, { useState } from "react";
import { Box, Typography, TextField, Alert } from "@mui/material";
import axios from "axios";

import typography from "../../cssUtils/typography";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import ButtonThree from "../../common/third-button";
import { GET_RCS_BRAND } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { INPUT_SX, CARD_SX, FieldLabel, ErrMsg } from "./RCSCreateBot";

// Brands are internal only (not submitted to Dotgo); a bot must belong to one.
// deferred: don't save here, hand the name to the bot form; the backend creates it together with the bot.
export default function RCSCreateBrand({ brands = [], deferred = false, initialName = "", onBack, onCreated }) {
  const { getValidToken } = useAuth();
  const [brandName, setBrandName] = useState(initialName);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");

  const validate = () => {
    const name = brandName.trim();
    let e = "";
    if (!name) e = "Brand name is required.";
    else if (name.length > 100) e = "Brand name must be 100 characters or fewer.";
    else if (brands.some((b) => b.brand_name.toLowerCase() === name.toLowerCase())) e = "A brand with this name already exists.";
    setError(e);
    return !e;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (deferred) {
      onCreated({ brand_name: brandName.trim() });
      return;
    }
    setSaving(true);
    setApiError("");
    try {
      const data = new FormData();
      data.append("brand_name", brandName.trim());
      const response = await axios.post(GET_RCS_BRAND, data, {
        headers: {
          Authorization: `Bearer ${await getValidToken()}`,
          "X-Tenant-ID": localStorage.getItem("tenant") || "",
        },
      });
      onCreated(response.data);
    } catch (err) {
      const resp = err.response?.data;
      setApiError(
        resp && typeof resp === "object"
          ? Object.entries(resp).map(([k, v]) => `${k}: ${[].concat(v).join(" ")}`).join(" | ")
          : "Failed to create brand. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560, mx: "auto" }}>
      <Typography sx={{ ...typography.h1, fontWeight: 700, color: ColorsConstants.primaryBlue100 }}>
        Create a brand
      </Typography>
      <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mb: 3 }}>
        {deferred
          ? "Every RCS bot belongs to a brand. Name your brand, then set up your first bot for it."
          : "Add a brand that your RCS bots can represent."}
      </Typography>

      {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

      <Box sx={CARD_SX}>
        <FieldLabel required hint="Bot names must include this brand name.">Brand Name</FieldLabel>
        <TextField
          fullWidth
          size="small"
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          placeholder="e.g. Acme Corp"
          error={!!error}
          sx={INPUT_SX}
          inputProps={{ maxLength: 100 }}
        />
        <ErrMsg>{error}</ErrMsg>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <ButtonThree onClick={onBack}>Back</ButtonThree>
        <ButtonOne disabled={saving} onClick={handleSubmit}>
          {deferred ? "Continue" : saving ? "Creating..." : "Create Brand"}
        </ButtonOne>
      </Box>
    </Box>
  );
}
